import React, { useState, useEffect } from 'react';
import { TrendingUp, Brain, AlertTriangle, CheckCircle, Clock, Target, Zap, Award } from 'lucide-react';
import { User } from '../../types';

interface PredictiveAnalyticsProps {
  currentUser: User;
  deployments: any[];
}

interface Prediction {
  type: 'success' | 'risk' | 'warning' | 'info';
  title: string;
  description: string;
  confidence: ConfidenceLevel;
  /** Number of historical records the prediction was derived from */
  sampleSize: number;
  /** What those records are, e.g. "completed deployments" */
  basis: string;
  icon: any;
}

type ConfidenceLevel = 'low' | 'medium' | 'high';

// Confidence reflects how much history backs a prediction, not a model score:
// fewer than 10 records is low, 10–29 medium, 30+ high.
const confidenceFromSample = (n: number): ConfidenceLevel =>
  n >= 30 ? 'high' : n >= 10 ? 'medium' : 'low';

const CONFIDENCE_STYLES: Record<ConfidenceLevel, string> = {
  high: 'text-emerald-700 border-emerald-200',
  medium: 'text-amber-700 border-amber-200',
  low: 'text-gray-600 border-gray-200'
};

const PredictiveAnalytics: React.FC<PredictiveAnalyticsProps> = ({ deployments }) => {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [successRate, setSuccessRate] = useState(0);
  const [riskScore, setRiskScore] = useState(0);

  useEffect(() => {
    analyzePredictions();
  }, [deployments]);

  const analyzePredictions = () => {
    if (!deployments || deployments.length === 0) return;

    // Calculate success rate
    const deployed = deployments.filter(d => d.status === 'deployed').length;
    const failed = deployments.filter(d => d.status === 'rolled_back').length;
    const total = deployed + failed;
    const rate = total > 0 ? (deployed / total) * 100 : 0;
    setSuccessRate(rate);

    // Calculate risk score based on patterns
    const pending = deployments.filter(d => d.status === 'pending').length;
    const critical = deployments.filter(d => d.priority === 'critical').length;
    const recentFailed = deployments.filter(d => {
      if (d.status !== 'rolled_back') return false;
      const daysSince = (Date.now() - new Date(d.updatedAt || d.createdAt).getTime()) / (1000 * 60 * 60 * 24);
      return daysSince <= 7;
    }).length;
    const recentFinished = deployments.filter(d => {
      if (d.status !== 'deployed' && d.status !== 'rolled_back') return false;
      const daysSince = (Date.now() - new Date(d.updatedAt || d.createdAt).getTime()) / (1000 * 60 * 60 * 24);
      return daysSince <= 7;
    }).length;

    const calculatedRisk = Math.min(100, (pending * 5) + (critical * 10) + (recentFailed * 15));
    setRiskScore(calculatedRisk);

    // Generate predictions
    const newPredictions: Prediction[] = [];

    // Success prediction
    if (rate > 85) {
      newPredictions.push({
        type: 'success',
        title: 'High Success Rate',
        description: `Your deployment success rate is ${rate.toFixed(1)}%! Keep up the excellent work.`,
        confidence: confidenceFromSample(total),
        sampleSize: total,
        basis: 'completed deployments (deployed or rolled back)',
        icon: CheckCircle
      });
    }

    // Risk predictions
    if (recentFailed > 2) {
      newPredictions.push({
        type: 'risk',
        title: 'Pattern Alert',
        description: `${recentFailed} deployments were rolled back in the last 7 days. Review your testing process.`,
        confidence: confidenceFromSample(recentFinished),
        sampleSize: recentFinished,
        basis: 'deployments completed in the last 7 days',
        icon: AlertTriangle
      });
    }

    // Timing prediction
    const hourCounts = new Map<number, { success: number; failed: number }>();
    deployments.forEach(d => {
      if (d.status === 'deployed' || d.status === 'rolled_back') {
        const hour = new Date(d.updatedAt || d.createdAt).getHours();
        if (!hourCounts.has(hour)) {
          hourCounts.set(hour, { success: 0, failed: 0 });
        }
        const count = hourCounts.get(hour)!;
        if (d.status === 'deployed') count.success++;
        else count.failed++;
      }
    });

    let bestHour = 0;
    let bestRate = 0;
    let bestHourSample = 0;
    hourCounts.forEach((count, hour) => {
      const total = count.success + count.failed;
      if (total > 3) {
        const rate = (count.success / total) * 100;
        if (rate > bestRate) {
          bestRate = rate;
          bestHour = hour;
          bestHourSample = total;
        }
      }
    });

    if (bestRate > 80) {
      newPredictions.push({
        type: 'info',
        title: 'Optimal Deployment Time',
        description: `Your deployments succeed ${bestRate.toFixed(0)}% of the time around ${bestHour}:00. Consider scheduling critical deployments then.`,
        confidence: confidenceFromSample(bestHourSample),
        sampleSize: bestHourSample,
        basis: `completed deployments around ${bestHour}:00`,
        icon: Clock
      });
    }

    // Module prediction
    const moduleStats = new Map<string, { success: number; failed: number }>();
    deployments.forEach(d => {
      if (d.modules && Array.isArray(d.modules)) {
        d.modules.forEach((mod: any) => {
          const modName = typeof mod === 'string' ? mod : mod.name;
          if (!moduleStats.has(modName)) {
            moduleStats.set(modName, { success: 0, failed: 0 });
          }
          const stats = moduleStats.get(modName)!;
          if (d.status === 'deployed') stats.success++;
          else if (d.status === 'rolled_back') stats.failed++;
        });
      }
    });

    let riskyModule = '';
    let worstRate = 100;
    let riskyModuleSample = 0;
    moduleStats.forEach((stats, module) => {
      const total = stats.success + stats.failed;
      if (total > 2) {
        const rate = (stats.success / total) * 100;
        if (rate < worstRate && rate < 60) {
          worstRate = rate;
          riskyModule = module;
          riskyModuleSample = total;
        }
      }
    });

    if (riskyModule) {
      newPredictions.push({
        type: 'warning',
        title: 'Module Risk Alert',
        description: `"${riskyModule}" has a ${worstRate.toFixed(0)}% success rate. Extra testing recommended.`,
        confidence: confidenceFromSample(riskyModuleSample),
        sampleSize: riskyModuleSample,
        basis: `completed deployments of "${riskyModule}"`,
        icon: Target
      });
    }

    // Deployment frequency
    const recentDeployments = deployments.filter(d => {
      const daysSince = (Date.now() - new Date(d.createdAt).getTime()) / (1000 * 60 * 60 * 24);
      return daysSince <= 7;
    }).length;

    if (recentDeployments > 15) {
      newPredictions.push({
        type: 'warning',
        title: 'High Deployment Frequency',
        description: `${recentDeployments} deployments in the last week. Consider batch deployments to reduce overhead.`,
        confidence: confidenceFromSample(recentDeployments),
        sampleSize: recentDeployments,
        basis: 'deployments created in the last 7 days',
        icon: Zap
      });
    }

    // Performance award
    if (rate > 90 && deployed > 10 && recentFailed === 0) {
      newPredictions.push({
        type: 'success',
        title: 'Performance Excellence',
        description: `Outstanding! ${deployed} successful deployments with ${rate.toFixed(0)}% success rate.`,
        confidence: confidenceFromSample(total),
        sampleSize: total,
        basis: 'completed deployments (deployed or rolled back)',
        icon: Award
      });
    }

    setPredictions(newPredictions);
  };

  const getColorByType = (type: string) => {
    switch (type) {
      case 'success': return 'bg-gradient-to-r from-emerald-50 to-white border-l-4 border-l-emerald-500 border-y border-r border-y-emerald-100 border-r-emerald-100';
      case 'risk': return 'bg-gradient-to-r from-rose-50 to-white border-l-4 border-l-rose-500 border-y border-r border-y-rose-100 border-r-rose-100';
      case 'warning': return 'bg-gradient-to-r from-amber-50 to-white border-l-4 border-l-amber-500 border-y border-r border-y-amber-100 border-r-amber-100';
      default: return 'bg-gradient-to-r from-sky-50 to-white border-l-4 border-l-sky-500 border-y border-r border-y-sky-100 border-r-sky-100';
    }
  };

  const getIconColorByType = (type: string) => {
    switch (type) {
      case 'success': return 'text-emerald-600 bg-emerald-100';
      case 'risk': return 'text-rose-600 bg-rose-100';
      case 'warning': return 'text-amber-600 bg-amber-100';
      default: return 'text-sky-600 bg-sky-100';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-indigo-600 to-fuchsia-600 rounded-lg shadow-md shadow-indigo-200">
          <Brain className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Predictive Analytics</h2>
          <p className="text-gray-600">AI-powered insights and predictions</p>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-emerald-50 via-white to-white p-6 rounded-xl shadow-md border border-emerald-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Success Rate</p>
              <p className="text-3xl font-bold text-emerald-600">{successRate.toFixed(1)}%</p>
              <p className="text-xs text-gray-500 mt-1">Based on historical data</p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg">
              <TrendingUp className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
          <div className="mt-4 w-full bg-emerald-100 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-emerald-400 to-emerald-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${successRate}%` }}
            />
          </div>
        </div>

        <div className={`p-6 rounded-xl shadow-md border ${
          riskScore > 50 ? 'bg-gradient-to-br from-rose-50 via-white to-white border-rose-100' :
          riskScore > 25 ? 'bg-gradient-to-br from-amber-50 via-white to-white border-amber-100' :
          'bg-gradient-to-br from-emerald-50 via-white to-white border-emerald-100'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Risk Score</p>
              <p className={`text-3xl font-bold ${riskScore > 50 ? 'text-rose-600' : riskScore > 25 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {riskScore.toFixed(0)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Current deployment risk</p>
            </div>
            <div className={`p-3 rounded-lg ${riskScore > 50 ? 'bg-rose-100' : riskScore > 25 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
              <AlertTriangle className={`h-8 w-8 ${riskScore > 50 ? 'text-rose-600' : riskScore > 25 ? 'text-amber-600' : 'text-emerald-600'}`} />
            </div>
          </div>
          <div className={`mt-4 w-full rounded-full h-2 ${riskScore > 50 ? 'bg-rose-100' : riskScore > 25 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
            <div
              className={`h-2 rounded-full transition-all duration-500 ${
                riskScore > 50 ? 'bg-gradient-to-r from-rose-400 to-rose-600' :
                riskScore > 25 ? 'bg-gradient-to-r from-amber-400 to-amber-600' :
                'bg-gradient-to-r from-emerald-400 to-emerald-600'
              }`}
              style={{ width: `${riskScore}%` }}
            />
          </div>
        </div>
      </div>

      {/* Predictions */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Brain className="h-5 w-5 text-indigo-600" />
          AI Predictions & Insights
        </h3>

        {predictions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Brain className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Not enough data for predictions yet.</p>
            <p className="text-sm mt-1">Deploy more to see AI insights!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {predictions.map((prediction, idx) => {
              const Icon = prediction.icon;
              return (
                <div
                  key={idx}
                  className={`p-4 rounded-lg ${getColorByType(prediction.type)} transition-all hover:shadow-md`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-1.5 rounded-lg ${getIconColorByType(prediction.type)}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-gray-900">{prediction.title}</h4>
                        <span
                          className={`text-xs font-medium px-2 py-1 bg-white/80 border rounded-full capitalize ${CONFIDENCE_STYLES[prediction.confidence]}`}
                          title="Confidence is based on how many historical records back this insight: <10 low, 10–29 medium, 30+ high"
                        >
                          {prediction.confidence} confidence
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{prediction.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Based on {prediction.sampleSize} {prediction.basis}.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ML Model Info */}
      <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-fuchsia-50 rounded-xl p-6 border border-indigo-200">
        <div className="flex items-start gap-3">
          <Brain className="h-6 w-6 text-indigo-600 mt-1" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">About This AI Model</h4>
            <p className="text-sm text-gray-700 mb-2">
              These insights come from rule-based analysis of your deployment history (success/rollback counts by time, module and recency). Each insight's confidence reflects how many records it is based on.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-xs text-gray-600">Algorithm</p>
                <p className="text-sm font-semibold text-gray-900">Rule-based pattern analysis</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-xs text-gray-600">Data Points</p>
                <p className="text-sm font-semibold text-gray-900">{deployments.length} deployments</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-xs text-gray-600">Confidence scale</p>
                <p className="text-sm font-semibold text-gray-900">&lt;10 low · 10–29 medium · 30+ high</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-xs text-gray-600">Updated</p>
                <p className="text-sm font-semibold text-gray-900">Real-time</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictiveAnalytics;
